/**
 * UI strings for the Lernpfad demo, DE + EN.
 *
 * A LOCAL dictionary rather than next-intl on purpose. The app-wide i18n system
 * resolves one locale per request from `app-config/clients/*\/locales/`, which
 * cannot give this page an in-page toggle — and adding demo keys to four
 * client catalogues would drag in the namespace indent-width trap documented in
 * the root CLAUDE.md for strings that only ever appear here.
 *
 * Content strings (Baustein titles, skills, CVs, …) live in `content-en.ts`,
 * keyed by id, so the domain data stays monolingual and framework-free.
 */

export type Lang = "de" | "en"

export const LANGS: Lang[] = ["de", "en"]

type Dict = Record<string, string>

const de = {
    // Shell
    "app.title": "Lernpfad",
    "app.badge": "Demo",
    "app.subtitle":
        "Aus Funktion, Pflichten, Kompetenzprofil und Beurteilung wird ein individuelles Weiterbildungsprogramm zusammengestellt — sequenziert, budgetiert und für jeden Baustein begründet.",
    "app.language": "Sprache",
    "demo.viewSwitch": "Ansicht",

    // Landing (Compass)
    "landing.claim": "Personalisierte Weiterbildung. Aus jedem LMS.",
    "landing.sub":
        "Compass verbindet sich mit Ihrem bestehenden Lernsystem, versteht Pflichten und Kompetenzlücken — und stellt für jede Person ein begründetes Programm zusammen.",
    "landing.cta": "Demo starten",
    "landing.wall.title": "Funktioniert mit Ihrem LMS",
    "landing.wall.sub":
        "Ein Anschluss, vier Flächen: Katalog, Transcript, Launch, Zuweisung. Das LMS bleibt das System of Record.",
    "landing.how.title": "So funktioniert es",
    "landing.how.1.title": "Verbinden",
    "landing.how.1.body": "LMS anschliessen — Katalog und Abschlüsse fliessen automatisch ein.",
    "landing.how.2.title": "Verstehen",
    "landing.how.2.body": "Pflichten, Fristen und Kompetenzlücken pro Funktion erkennen.",
    "landing.how.3.title": "Begleiten",
    "landing.how.3.body": "Ein Programm pro Person — sequenziert, budgetiert, begründet.",
    "landing.personas.title": "Eine Engine, drei Sichten",
    "landing.persona.employee": "Mein Lernpfad mit einem „Warum das?“ für jeden Baustein.",
    "landing.persona.manager": "Vorschläge fürs MAG statt leerer Seiten.",
    "landing.persona.hr": "Abdeckung, Lücken und Katalog-Bedarf über die ganze Organisation.",
    "landing.persona.open": "Ansehen",
    // Guided path — the demo has five views and no story; this supplies one.
    "tour.start": "Geführte Tour",
    "tour.step": "Schritt {current} von {total}",
    "tour.next": "Weiter",
    "tour.done": "Fertig",
    "tour.skip": "Überspringen",
    "tour.plan.title": "Ein Plan, vier Gründe",
    "tour.plan.body":
        "Sandra Bergers Programm. Jeder Baustein trägt seinen Grund: Pflicht, Voraussetzung, Kompetenzlücke oder Karriereziel — nichts steht unbegründet da.",
    "tour.levers.title": "Ein Regler, neu gerechnet",
    "tour.levers.body":
        "Wir haben ihr Pensum auf 60 % gesetzt. Das Quartalsbudget schrumpft mit — der Plan wird neu gerechnet und nicht etwa gefiltert.",
    "tour.manager.title": "Dieselben Daten, andere Seite",
    "tour.manager.body":
        "Die Führungssicht: Vorschläge fürs Mitarbeitergespräch und die Bestätigung, was ein Baustein in der Praxis verändert hat.",
    "tour.hr.title": "Die ganze Organisation",
    "tour.hr.body":
        "Abdeckung, überfällige Pflichten und Katalog-Lücken über alle Mitarbeitenden — nicht Profil für Profil.",
    "tour.model.title": "Das Modell wechseln",
    "tour.model.body":
        "Wir haben LTEM aktiviert. Dieselben Abschlüsse, strengerer Massstab: „Belegt nach Vorgabe“ bricht ein, ohne dass sich eine einzige Person verändert hat.",
    "tour.lms.title": "Was Mitarbeitende sehen",
    "tour.lms.body":
        "Zum Schluss die Gegenseite: „So sehen es Ihre Mitarbeitenden“ öffnet das simulierte Lernportal mit Katalog, Kurs und Nachweis.",
    "landing.footnote":
        "Demo — synthetische Daten auf realer Organisationsstruktur, keine Live-LMS-Verbindung.",

    // Integrations view
    "int.tab": "Integrationen",
    "int.title": "Verbindungen",
    "int.subtitle": "Compass liest Katalog und Transcript — das LMS bleibt das System of Record.",
    "int.connect": "Verbinden",
    "int.disconnect": "Trennen",
    "int.connected": "verbunden",
    "int.available": "verfügbar",
    "int.coming": "geplant",
    "int.courses": "{count} Kurse",
    "int.lastSync": "Letzter Sync: {when}",
    "int.justNow": "gerade eben",
    "cap.catalog": "Katalog",
    "cap.transcript": "Transcript",
    "cap.launch": "Launch",
    "cap.assign": "Zuweisung",

    // Connect wizard
    "wiz.title": "Moodle verbinden",
    "wiz.step.access": "Zugang",
    "wiz.step.discovery": "Prüfung",
    "wiz.step.catalog": "Katalog",
    "wiz.step.transcript": "Transcript",
    "wiz.final": "Fertig",
    "wiz.url": "LMS-URL",
    "wiz.token": "Web-Service-Token",
    "wiz.tokenHint": "Nur-Lese-Rolle, auf Kurs- und Abschlussfunktionen beschränkt.",
    "wiz.next": "Weiter",
    "wiz.start": "Verbindung prüfen",
    "wiz.import": "Katalog importieren",
    "wiz.sync": "Transcript synchronisieren",
    "wiz.done": "Fertigstellen",
    "wiz.discovery.1": "Web Services erreichbar",
    "wiz.discovery.2": "core_course_get_courses_by_field",
    "wiz.discovery.3": "core_course_get_contents",
    "wiz.discovery.4": "core_completion_get_activities_completion_status",
    "wiz.discovery.5": "Berechtigung: nur Lesen",
    "wiz.discovery.6": "Abschluss-Tracking aktiv",
    "wiz.catalog.running": "Importiere Kurse …",
    "wiz.catalog.mapped": "Kompetenzen zugeordnet",
    "wiz.catalog.review": "zur Prüfung",
    "wiz.catalog.done": "{total} Kurse importiert · {mapped} automatisch zugeordnet · {review} zur Prüfung",
    "wiz.transcript.running": "Lese Abschlüsse für {count} Mitarbeitende …",
    "wiz.transcript.found": "{count} Abschlüsse übernommen",
    "wiz.transcript.note":
        "Moodle liefert Abschlüsse pro Person und Kurs — der Sync läuft nachts, plus auf Abruf.",
    "wiz.success": "Moodle ist verbunden. Katalog und Transcript fliessen jetzt in Compass ein.",

    // LMS player (mock LTI)
    "player.launch": "Hier starten",
    "player.via": "via LTI 1.3",
    "player.chrome": "learn.acme-bank.ch · LTI 1.3",
    "player.slide": "Abschnitt {current} von {total}",
    "player.nextSlide": "Weiter",
    "player.finish": "Abschlusstest bestehen",
    "player.passed": "Abschlusstest bestanden",
    "player.close": "Schliessen",
    "toast.ags": "Moodle meldet Abschluss (AGS) — Transcript aktualisiert.",
    "toast.agsNote": "Produktiv bis zu 30 Min. Verzögerung — in der Demo sofort.",
    "item.viaLti": "Abschluss via LTI",

    // Source ripples
    "src.catalogLine": "Katalog: Moodle ✓ · {count} Kurse · Stand heute",
    "src.hrLine": "Quelle: Moodle ({count} Kurse) · {sync}",
    "src.demoData": "Katalog: Demo-Daten",
    "levers.moodleTranscript": "Moodle Transcript",
    "levers.moodleTranscriptHint": "System of Record — immer aktiv",

    // HR sync
    "hr.syncNow": "Jetzt synchronisieren",
    "hr.syncing": "Synchronisiere …",
    "hr.syncDelta": "+{count} Abschlüsse aus Moodle übernommen",


    // Personas
    "persona.employee": "Mitarbeitende",
    "persona.manager": "Führungskraft",
    "persona.hr": "HR / L&D",

    // Levers
    "levers.title": "Live-Parameter",
    "levers.reset": "zurücksetzen",
    "levers.pensum": "Pensum",
    "levers.pensumReal": "real {value}%",
    "levers.budget": "Budget: {hours} h pro Quartal",
    "levers.timeBudget": "Zeitbudget bei 100%",
    "levers.hoursPerQuarter": "{hours} h/Quartal",
    "levers.aspiration": "Karriereziel",
    "levers.asOf": "Stichtag",
    "levers.today": "heute",
    "levers.monthsAhead": "+{months} Monate — Refresher laufen ab",
    "levers.horizon": "Planungshorizont",
    "levers.months": "{months} Monate",
    "levers.sources": "Datenquellen",
    "levers.sourcesHint":
        "Was der Vorschlag verwenden darf. Ohne Beurteilungsdaten bleibt der Plan gültig — er stützt sich dann auf Funktion und Pflichten.",
    "levers.sensitive": "heikel",

    // Sources
    "source.stellenprofil": "Stellenprofil",
    "source.lernhistorie": "Lernhistorie",
    "source.cv": "Lebenslauf",
    "source.mag": "Beurteilung / MAG",
    "source.selbsteinschaetzung": "Selbsteinschätzung",

    // Employee view
    "employee.title": "Mein Lernpfad",
    "employee.stat.items": "Bausteine",
    "employee.stat.hours": "Aufwand total",
    "employee.stat.mandatory": "davon Pflicht",
    "employee.stat.overdue": "überfällig",
    "employee.overdueTitle": "{count} Pflichtschulung überfällig",
    "employee.overdueTitle.plural": "{count} Pflichtschulungen überfällig",
    "employee.overdueBody":
        "Diese Bausteine werden unabhängig vom Zeitbudget eingeplant. Ein Quartal darf dadurch über Budget liegen — das ist ein sichtbares Planungsproblem und keine stille Auslassung.",
    "employee.quarterBudget": "{planned} / {budget} h",
    "employee.overBudget": "über Budget",
    "employee.deferred": "Zurückgestellt",
    "employee.deferredHint":
        "Passt im gewählten Horizont nicht ins Zeitbudget. Bewusst ausgewiesen statt weggelassen.",
    "employee.noCatalog": "Kein passender Baustein im Katalog",
    "employee.gapTable": "Kompetenzabgleich",
    "employee.load": "Auslastung pro Quartal",
    "employee.gapsOpen": "{count} offen",
    "employee.col.skill": "Kompetenz",
    "employee.col.current": "Ist",
    "employee.col.required": "Soll",
    "employee.col.origin": "Herkunft",
    "employee.col.evidence": "Beleg",

    // Item card
    "item.why": "Warum das?",
    "item.done": "erledigt",
    "item.hours": "{hours} h",
    "item.due": "Frist: {date}",
    "item.overdue": "überfällig",
    "reason.pflicht": "Pflicht",
    "reason.auffrischung": "Auffrischung",
    "reason.voraussetzung": "Voraussetzung",
    "reason.luecke": "Lücke",
    "reason.ziel": "Entwicklungsziel",
    "origin.regulatorik": "Regulatorik",
    "origin.rolle": "Funktion",
    "origin.mag": "Beurteilung",
    "origin.ziel": "Ziel",

    // Manager view
    "manager.title": "Team-Entwicklung",
    "manager.subtitle": "{count} Mitarbeitende",
    "manager.disclaimer":
        "Vorschlag für das Mitarbeitergespräch — kein zugewiesener Plan. Die Führungskraft passt an, bespricht und übernimmt. Es findet keine automatisierte Einzelentscheidung statt.",
    "manager.col.person": "Mitarbeitende:r",
    "manager.col.role": "Funktion",
    "manager.col.pensum": "Pensum",
    "manager.col.items": "Bausteine",
    "manager.col.hours": "Aufwand",
    "manager.col.mandatory": "Pflicht",
    "manager.overdueBadge": "{count} überfällig",
    "manager.openBadge": "{count} offen",
    "manager.fulfilled": "erfüllt",
    "manager.proposalFor": "Vorschlag für {name}",
    "manager.adopt": "In MAG übernehmen",
    "manager.noAction": "Kein Handlungsbedarf im gewählten Horizont.",
    "manager.basis": "Grundlage: Beurteilungsauszug",
    "manager.matrix": "Pflichtstatus im Team",
    "manager.matrixHint": "Spalte anfahren, um die Pflicht zu benennen",
    "manager.effort": "Aufwand pro Mitarbeitende:r",

    // HR view
    "hr.title": "L&D Cockpit",
    "hr.subtitle":
        "{count} Mitarbeitende · reale Organisationsstruktur, synthetische HR-Attribute",
    "hr.stat.coverage": "Pflicht-Abdeckung",
    "hr.stat.coverageHint": "ohne offene Pflichtschulung",
    "hr.stat.open": "mit offener Pflicht",
    "hr.stat.openHint": "davon {count} überfällig",
    "hr.stat.effort": "Geplanter Aufwand",
    "hr.stat.effortHint": "≈ {years} FTE-Jahre",
    "hr.stat.catalogGaps": "Katalog-Lücken",
    "hr.stat.catalogGapsHint": "Kompetenzen ohne Baustein",
    "hr.coverage": "Pflichtschulungen — Abdeckung",
    "hr.overdueSuffix": "{count} überfällig",
    "hr.topGaps": "Grösste Kompetenzlücken",
    "hr.noBaustein": "kein Baustein",
    "hr.catalogGapTitle": "Katalog-Lücke",
    "hr.catalogGapBody":
        "Für {skills} existiert kein Baustein — betrifft {count} Mitarbeitende. Das ist der Hinweis, was L&D als Nächstes entwickeln sollte.",
    "hr.demand": "Nachfrage nach Bausteinen",
    "hr.byUnit": "Aufwand nach Organisationseinheit",
    "hr.col.unit": "Einheit",
    "hr.col.headcount": "Mitarbeitende",
    "hr.col.effort": "Aufwand",
    "hr.col.overdue": "überfällig",
    "hr.loadError": "Auswertung konnte nicht geladen werden.",

    // Skill levels
    "level.0": "keine",
    "level.1": "Grundlagen",
    "level.2": "Anwendung",
    "level.3": "Fortgeschritten",
    "level.4": "Experte",

    // Formats
    "format.e-learning": "E-Learning",
    "format.praesenz": "Präsenz",
    "format.webinar": "Webinar",
    "format.selbststudium": "Selbststudium",
    "format.on-the-job": "On-the-job",
    "format.social": "Soziales Lernen",

    // Rationale
    "rat.mandatory": "Pflicht für Ihre Funktion als {role}: {obligation} ({basis}).",
    "rat.dueBy": "Fällig bis {date}.",
    "rat.prerequisite":
        "Voraussetzung — ohne diesen Baustein können Sie die darauf aufbauenden Inhalte nicht belegen.",
    "rat.closesGoal": "Zahlt auf Ihr Entwicklungsziel ein",
    "rat.closesAlso": "Schliesst zusätzlich",
    "rat.closes": "Schliesst",
    "rat.fallback": "Empfohlen aufgrund der Anforderungen Ihrer Funktion als {role}.",
    "rat.src.stellenprofil": "Laut Stellenprofil",
    "rat.src.lernhistorie": "Laut Lernhistorie",
    "rat.src.cv": "Laut Lebenslauf",
    "rat.src.mag": "Laut Ihrer letzten Beurteilung",
    "rat.src.selbsteinschaetzung": "Laut Selbsteinschätzung",
    // Steuerung — Rahmenwerke
    "persona.steuerung": "Steuerung",
    "steuer.title": "Steuerung des Empfehlungssystems",
    "steuer.subtitle": "Rahmenwerk wählen, Parameter feinjustieren — jede Änderung berechnet die Programme neu.",
    "steuer.tab.frameworks": "Rahmenwerke",
    "steuer.tab.params": "Parameter",
    "steuer.family.ladder": "Wirkungsmodelle",
    "steuer.family.ladderHint": "Konkurrierende Antworten auf dieselbe Frage — genau eines aktiv.",
    "steuer.family.overlay": "Ergänzende Verfahren",
    "steuer.family.overlayHint": "Setzen ein Wirkungsmodell voraus und lassen sich frei kombinieren.",
    "steuer.family.design": "Lerndesign",
    "steuer.family.designHint": "Bestimmen, wie ein Programm aufgebaut wird — unabhängig von der Messung.",
    "steuer.changes": "Was sich ändert",
    "steuer.watch": "Kennzahl im Blick",
    "steuer.source": "Quelle",
    "steuer.active": "Aktiv",
    "steuer.select": "Auswählen",
    "steuer.deselect": "Entfernen",
    "steuer.adjusted": "angepasst",
    "steuer.conflict": "Nur ein Wirkungsmodell kann aktiv sein. {names} schliessen sich gegenseitig aus.",
    "steuer.none": "Kein Rahmenwerk gewählt — es gilt die Standardeinstellung des Hauses.",
    "steuer.radar": "Schwerpunkte der Auswahl",
    "steuer.rule.one": "Genau eines",
    "steuer.rule.any": "Frei kombinierbar",
    "steuer.writes.one": "{count} Parameter",
    "steuer.writes.other": "{count} Parameter",
    "steuer.wrote.one": "Setzt {count} Parameter:",
    "steuer.wrote.other": "Setzt {count} Parameter:",
    "steuer.standard": "Standard des Hauses",
    "steuer.preview": "Vorschau",
    "steuer.swap.replacedBy": "ersetzt durch {name}",
    "steuer.radarRule": "Stärkster Wert je Achse, nicht Summe.",
    "steuer.axis.nachweis": "Nachweis-Strenge",
    "steuer.axis.kosten": "Kostenfokus",
    "steuer.axis.transfer": "Transfer-Fokus",
    "steuer.axis.modalitaet": "Modalitätsvielfalt",
    "steuer.axis.wiederholung": "Wiederholung",
    // Steuerung — Parameter
    "steuer.cluster.budget": "Zeit und Budget",
    "steuer.cluster.priority": "Priorisierung",
    "steuer.cluster.evidence": "Nachweis und Wirkung",
    "steuer.cluster.duties": "Pflichten",
    "steuer.cluster.gaps": "Lücken und Niveau",
    "steuer.cluster.sources": "Datenquellen und Vertrauen",
    "steuer.cluster.sequencing": "Sequenzierung und Wiederholung",
    "steuer.cluster.mix": "Modalitätsmix",
    "steuer.cluster.cost": "Kostensatz",
    "steuer.p.hoursPerQuarter": "Stunden pro Quartal bei 100 %",
    "steuer.p.scaleByPensum": "Budget mit dem Pensum skalieren",
    "steuer.p.scaleByPensumHint": "Teilzeit erhält anteilig weniger Lernzeit — Pflichten bleiben davon unberührt.",
    "steuer.p.periodMonths": "Länge einer Planungsperiode",
    "steuer.p.months": "Monate",
    "steuer.p.originWeight": "Gewicht je Herkunft",
    "steuer.p.originWeightHint": "Woher eine Anforderung stammt, bestimmt ihren Wert in der Rangfolge.",
    "steuer.p.denominator": "Rangfolge rechnet pro",
    "steuer.p.denominator.hours": "Stunde",
    "steuer.p.denominator.usd": "Dollar",
    "steuer.p.denominator.flat": "Nutzen absolut",
    "steuer.p.floor": "Was als Nachweis gilt",
    "steuer.p.floorHint": "Die Stufe, ab der ein Abschluss als Beleg zählt.",
    "steuer.p.gateSkillCredit": "Kompetenz nur mit Nachweis anerkennen",
    "steuer.p.gateObligation": "Pflicht nur mit Nachweis als erfüllt werten",
    "steuer.p.gateObligationHint": "Regulatorische Pflichten verlangen in der Regel einen Nachweis der Kenntnisse — eine Teilnahmebestätigung ist keiner.",
    "steuer.p.requireBehaviour": "Bestätigte Anwendung im Alltag verlangen",
    "steuer.p.reactionTarget": "Zielwert Anwendungsabsicht (Stufe 1)",
    "steuer.p.learningPass": "Bestehensgrenze (Stufe 2)",
    "steuer.p.behaviourWindow": "Bestätigungsfenster (Stufe 3)",
    "steuer.p.resultsWindow": "Auswertungsfenster (Stufe 4)",
    "steuer.p.days": "Tage",
    "steuer.p.neverDrop": "Pflichten nie verdrängen",
    "steuer.p.neverDropHint": "Ein überbuchtes Quartal ist ein sichtbares Planungsproblem, eine stillschweigend gestrichene Pflicht eine Feststellung der Revision.",
    "steuer.p.leadTime": "Vorlaufzeit-Faktor",
    "steuer.p.leadTimeHint": "Multiplikator auf die gesetzliche Vorlaufzeit jeder Pflicht.",
    "steuer.p.minSize": "Kleinste Lücke, die zählt",
    "steuer.p.maxLevelJump": "Grösster Niveausprung je Baustein",
    "steuer.p.tooBasic": "Toleranz für zu einfache Bausteine",
    "steuer.p.trust": "Vertrauen",
    "steuer.p.minTrust": "Mindestvertrauen",
    "steuer.p.sourceMode": "Bei geringem Vertrauen",
    "steuer.p.sourceMode.gate": "Angabe verwerfen",
    "steuer.p.sourceMode.discount": "Niveau abwerten",
    "steuer.p.expand": "Voraussetzungen mitplanen",
    "steuer.p.maxDepth": "Maximale Verkettungstiefe",
    "steuer.p.maxItems": "Bausteine je Periode",
    "steuer.p.maxHours": "Stunden je Baustein",
    "steuer.p.maxCategories": "Themenfelder gleichzeitig",
    "steuer.p.maxCategoriesHint": "Interleaving-Grenze: vier Themen parallel wirken schlechter als zwei nacheinander.",
    "steuer.p.deferralMode": "Verschieben, wenn es nicht passt",
    "steuer.p.deferralMode.chain": "ganze Kette prüfen",
    "steuer.p.deferralMode.period": "Periode für Periode",
    "steuer.p.reinforcement": "Auffrischung einplanen",
    "steuer.p.afterDays": "Abstand zur Schulung",
    "steuer.p.boosterHours": "Dauer der Auffrischung",
    "steuer.p.mixEnabled": "Modalitätsmix steuern",
    "steuer.p.mixTarget": "Zielverteilung",
    "steuer.p.strength": "Steuerungsstärke",
    "steuer.mod.on-the-job": "Bei der Arbeit",
    "steuer.mod.social": "Im Austausch",
    "steuer.mod.formal": "Im Kurs",
    "steuer.p.perHour": "USD pro Stunde",
    "steuer.p.fixed": "USD fix",
    "steuer.p.opportunity": "Arbeitszeit der Lernenden",
    "steuer.p.opportunityHint": "Opportunitätskosten pro Lernstunde. Null bedeutet: nicht mitgerechnet.",
    "steuer.p.honourOverride": "Hinterlegte Preise bevorzugen",
    "steuer.inactive": "Ohne Wirkung, solange die Rangfolge nicht in Dollar rechnet.",
    "steuer.mixInactive": "Ohne Wirkung, solange der Modalitätsmix nicht gesteuert wird.",
    // Steuerung — Auswirkung
    "steuer.impact": "Auswirkung",
    "steuer.impact.subtitle": "Berechnet auf dem echten Modell, nicht geschätzt.",
    "steuer.impact.person": "Beispiel-Mitarbeitende",
    "steuer.impact.population": "Gesamtorganisation",
    "steuer.impact.items": "Bausteine",
    "steuer.impact.hours": "Stunden",
    "steuer.impact.deferred": "Zurückgestellt",
    "steuer.impact.coverage": "Pflicht-Abdeckung",
    "steuer.impact.overdue": "Überfällig",
    "steuer.impact.cost": "Programmkosten",
    "steuer.impact.added": "neu",
    "steuer.impact.removed": "entfällt",
    "steuer.impact.moved": "verschoben",
    "steuer.impact.unchanged": "Keine Änderung gegenüber dem Standard.",
    "steuer.impact.loading": "wird berechnet",
    "steuer.impact.evidenceShare": "Belegt nach Vorgabe",
    "steuer.impact.same": "unverändert",
    "steuer.chart.distribution": "Verteilung der Lernstunden",
    "steuer.chart.distributionHint": "Alle {count} Mitarbeitenden nach geplantem Aufwand. Ein Mittelwert verbirgt, ob sich alle etwas bewegen oder wenige sehr viel.",
    "steuer.chart.flow": "Wohin das Budget fliesst",
    "steuer.chart.flowHint": "Von der Herkunft der Anforderung zur Lernform. Breite entspricht Stunden.",
    "steuer.chart.standard": "Standard",
    "steuer.chart.tuned": "Aktuelle Einstellung",
    "steuer.chart.hoursAxis": "Geplante Stunden je Person",
    "steuer.chart.people": "Mitarbeitende",
    "steuer.overrides": "{count} Parameter weichen vom Standard ab",
    "steuer.overrides.none": "Alle Parameter auf Standard",
    "steuer.reset": "Auf Standard zurücksetzen",
    "steuer.presetNote": "Ein Rahmenwerk setzt Parameter. Im Reiter Parameter bleibt jede einzelne davon sichtbar und korrigierbar.",
    "steuer.cluster.governance": "Governance",
    "steuer.governance.body": "Der Composer arbeitet deterministisch: gleiche Daten und gleiche Parameter ergeben denselben Plan, nachvollziehbar gegenüber der Revision. Kein Baustein verschwindet stillschweigend — was nicht ins Budget passt, erscheint als zurückgestellt. Jede Empfehlung trägt ihre Begründung. Das Sprachmodell liest Freitext und formuliert; es entscheidet nicht, wer was lernen muss.",
    "instrument.abschluss": "Abschluss",
    "instrument.wissenstest": "Wissenstest",
    "instrument.entscheidungstest": "Entscheidungstest",
    "instrument.transfernachweis": "Transfernachweis",
    "wirkung.title": "Wirkungsmessung",
    "wirkung.evidenceMix": "Was die Lernhistorie belegt",
    "wirkung.evidenceMixHint": "{count} erfasste Abschlüsse, nach Beleggüte.",
    "wirkung.floorNote": "Bei der aktuell verlangten Stufe „{floor}“ erfüllen {share} % der Abschlüsse den Nachweis.",
    "wirkung.chain": "Nachweiskette",
    "wirkung.chainHint": "Anteil der Abschlüsse, für die ein Beleg der jeweiligen Stufe vorliegt. Die Stufen sind unabhängige Messungen, kein Trichter — Pflichtschulungen erfassen fast immer ein Testergebnis, eine Rückmeldung deutlich seltener.",
    "wirkung.l1": "Rückmeldung erfasst",
    "wirkung.l1target": "Anwendungsabsicht auf Zielwert",
    "wirkung.l2": "Prüfung bestanden",
    "wirkung.bridge": "Transferleistung erbracht",
    "wirkung.l3": "Anwendung bestätigt",
    "wirkung.l3verified": "davon durch Führungskraft bestätigt",
    "wirkung.l4": "Stufe 4 — Wirkung im Geschäft",
    "wirkung.l4Hint": "Gemessen {from}–{to} Tage nach Abschluss.",
    "wirkung.indicator": "Indikator",
    "wirkung.before": "Vorher",
    "wirkung.after": "Aktuell",
    "wirkung.change": "Veränderung",
    "wirkung.kpi.auditfeststellungen": "Feststellungen aus Audits",
    "wirkung.kpi.dokumentation": "Nacharbeit an Kundendokumenten",
    "wirkung.kpi.fehlerquote": "Fehlerquote in Kernprozessen",
    "wirkung.kpi.produktivitaet": "Tage bis zur vollen Produktivität",
    "wirkung.kpi.servicequalitaet": "Servicequalität (Kundenbefragung)",
    "wirkung.benefit": "Bereinigter Nutzen",
    "wirkung.cost": "Programmkosten",
    "wirkung.roi": "ROI",
    "wirkung.method": "Bruttonutzen USD {gross}, bereinigt um den Isolationsfaktor ({isolation} %) und den Konfidenzabschlag ({confidence} %) nach Phillips. Nutzen-Kosten-Verhältnis {bcr}. Alle Indikatoren skalieren mit dem bestätigten Transfer ({transfer} %) — nicht mit der Teilnahme.",
    "wirkung.queue": "Transfer bestätigen",
    "wirkung.queueHint": "Fällig {from}–{to} Tage nach Abschluss. Eine Bestätigung wird sofort zu Stufe-3-Nachweis und verändert den Plan der Person.",
    "wirkung.queueEmpty": "Zurzeit steht keine Bestätigung an.",
    "wirkung.completedOn": "abgeschlossen am",
    "wirkung.confirmed": "bestätigt",
    "wirkung.drivers": "Welche Unterstützung war vorhanden?",
    "wirkung.driver.monitor": "Beobachtet",
    "wirkung.driver.reinforce": "Verstärkt",
    "wirkung.driver.encourage": "Ermutigt",
    "wirkung.driver.reward": "Anerkannt",
    "wirkung.applied.yes": "Wird angewendet",
    "wirkung.applied.no": "Wird nicht angewendet",
    "wirkung.kette": "Nachweiskette",
    "wirkung.none": "kein Beleg",
    "lms.shell.title": "Lernportal",
    "lms.shell.close": "Schliessen",
    "lms.shell.tab.catalog": "Katalog",
    "lms.shell.tab.transcript": "Meine Kurse",
    "lms.shell.back": "Zurück zum Katalog",
    "lms.catalog.search": "Kurse durchsuchen…",
    "lms.catalog.count": "{count} Kurse",
    "lms.catalog.filter.format": "Format",
    "lms.catalog.filter.category": "Themenfeld",
    "lms.catalog.filter.provider": "Anbieter",
    "lms.catalog.filter.all": "Alle",
    "lms.catalog.empty": "Keine Kurse gefunden.",
    "lms.detail.durationLabel": "Dauer",
    "lms.detail.formatLabel": "Format",
    "lms.detail.providerLabel": "Anbieter",
    "lms.detail.levelLabel": "Einstiegsniveau",
    "lms.detail.skillsLabel": "Vermittelte Kompetenzen",
    "lms.detail.prerequisitesLabel": "Voraussetzungen",
    "lms.detail.enrolled": "{count} Teilnehmende bisher",
    "lms.detail.courseId": "Kurs-ID {id}",
    "lms.detail.start": "Kurs starten",
    "lms.detail.notOnline": "Dieser Kurstyp wird nicht online durchgeführt. Anmeldung über {provider}.",
    "lms.detail.alreadyDone": "Bereits abgeschlossen",
    "lms.player.quizIntro": "Kurzer Wissenscheck — {count} Fragen",
    "lms.player.question": "Frage {current} von {total}",
    "lms.player.showResult": "Ergebnis anzeigen",
    "lms.player.reactionIntro": "Noch zwei kurze Fragen zu Ihrer Einschätzung",
    "lms.player.reaction.relevance": "Wie relevant war dieser Kurs für Ihre Arbeit?",
    "lms.player.reaction.apply": "Wie wahrscheinlich ist es, dass Sie das Gelernte anwenden?",
    "lms.player.scaleLow": "gering",
    "lms.player.scaleHigh": "hoch",
    "lms.player.result.title": "Ergebnis",
    "lms.player.result.score": "{correct} von {total} richtig ({percent}%)",
    "lms.player.result.failed": "Wissenscheck nicht bestanden — der Abschluss wird trotzdem erfasst.",
    "lms.transcript.empty": "Noch keine abgeschlossenen Kurse.",
    "lms.transcript.completedOn": "Abgeschlossen am {date}",
    "lms.transcript.evidence.attendance": "Nur Teilnahme erfasst",
    "lms.transcript.evidence.learning": "{instrument} · {percent}%",
    "lms.transcript.evidence.reaction": "Rückmeldung erfasst",
    "lms.integrations.preview.title": "So sehen es Ihre Mitarbeitenden",
    "lms.integrations.preview.body": "Eine simulierte Ansicht des Lernportals, in das Compass hineinwirkt — Katalog, Kursstart und Transcript, aus Sicht der Lernenden.",
    "lms.integrations.preview.cta": "Lernportal ansehen",
    "instrument.abschluss.short": "Teilnahme",
    "instrument.wissenstest.short": "Wissenstest",
    "instrument.entscheidungstest.short": "Entscheidungstest",
    "instrument.transfernachweis.short": "Transfernachweis",
    "lms.quiz.regulatorik.0.stem": "Was ist das Hauptziel von {skill}?",
    "lms.quiz.regulatorik.0.correct": "Risiken für Kundschaft und Bank durch korrekte Anwendung der Vorgaben reduzieren",
    "lms.quiz.regulatorik.0.d1": "Ausschliesslich den administrativen Aufwand dokumentieren",
    "lms.quiz.regulatorik.0.d2": "Nur bei einer externen Prüfung relevant sein",
    "lms.quiz.regulatorik.0.d3": "Eine Formalität ohne Bezug zur täglichen Arbeit",
    "lms.quiz.regulatorik.1.stem": "Wie gehen Sie im Alltag korrekt mit {skill} um?",
    "lms.quiz.regulatorik.1.correct": "Die Vorgaben konsequent anwenden und Unklarheiten an die zuständige Stelle melden",
    "lms.quiz.regulatorik.1.d1": "Die Vorgaben nur anwenden, wenn Zeit dafür bleibt",
    "lms.quiz.regulatorik.1.d2": "Abweichungen selbständig entscheiden, ohne Rücksprache",
    "lms.quiz.regulatorik.1.d3": "Die Vorgaben gelten nur für neue Kundenbeziehungen",
    "lms.quiz.regulatorik.2.stem": "Sie stossen bei {skill} auf einen Grenzfall. Was ist richtig?",
    "lms.quiz.regulatorik.2.correct": "Den Fall dokumentieren und mit der zuständigen Fachstelle klären",
    "lms.quiz.regulatorik.2.d1": "Den Fall auf eigene Verantwortung entscheiden",
    "lms.quiz.regulatorik.2.d2": "Den Fall ignorieren, wenn er selten vorkommt",
    "lms.quiz.regulatorik.2.d3": "Abwarten, bis der Kunde nachfragt",
    "lms.quiz.fach.0.stem": "Worauf kommt es bei {skill} vor allem an?",
    "lms.quiz.fach.0.correct": "Die individuelle Situation der Kundschaft ganzheitlich einordnen",
    "lms.quiz.fach.0.d1": "Immer die margenstärkste Lösung empfehlen",
    "lms.quiz.fach.0.d2": "Ausschliesslich Standardprodukte anbieten",
    "lms.quiz.fach.0.d3": "Die fachliche Tiefe der Beratung möglichst gering halten",
    "lms.quiz.fach.1.stem": "Wie wenden Sie {skill} im Kundengespräch richtig an?",
    "lms.quiz.fach.1.correct": "Bedürfnisse klären, Optionen verständlich aufzeigen, Entscheidung der Kundschaft überlassen",
    "lms.quiz.fach.1.d1": "Direkt ein einzelnes Produkt vorschlagen",
    "lms.quiz.fach.1.d2": "Fachbegriffe ohne Erklärung verwenden",
    "lms.quiz.fach.1.d3": "Die Entscheidung stellvertretend treffen",
    "lms.quiz.fach.2.stem": "Eine Kundensituation bei {skill} ist ungewöhnlich. Was tun Sie?",
    "lms.quiz.fach.2.correct": "Den Fall mit einer Fachstelle oder erfahrenen Kolleg:in besprechen",
    "lms.quiz.fach.2.d1": "Die Standardlösung trotzdem anwenden",
    "lms.quiz.fach.2.d2": "Die Anfrage ablehnen",
    "lms.quiz.fach.2.d3": "Selbständig eine Ausnahme bewilligen",
    "lms.quiz.fuehrung.0.stem": "Was ist bei {skill} für eine Führungskraft zentral?",
    "lms.quiz.fuehrung.0.correct": "Klar kommunizieren und Verantwortung im Team sinnvoll verteilen",
    "lms.quiz.fuehrung.0.d1": "Alle Entscheidungen zentral selbst treffen",
    "lms.quiz.fuehrung.0.d2": "Konflikte möglichst lange vermeiden",
    "lms.quiz.fuehrung.0.d3": "Feedback nur einmal jährlich geben",
    "lms.quiz.fuehrung.1.stem": "Wie setzen Sie {skill} im Teamalltag um?",
    "lms.quiz.fuehrung.1.correct": "Regelmässig zuhören, Erwartungen klären und zeitnah Feedback geben",
    "lms.quiz.fuehrung.1.d1": "Nur bei Problemen das Gespräch suchen",
    "lms.quiz.fuehrung.1.d2": "Entscheidungen ohne Begründung kommunizieren",
    "lms.quiz.fuehrung.1.d3": "Die Verantwortung vollständig delegieren",
    "lms.quiz.fuehrung.2.stem": "Im Team entsteht bei {skill} eine schwierige Situation. Was ist der richtige erste Schritt?",
    "lms.quiz.fuehrung.2.correct": "Das direkte Gespräch suchen und die Sichtweisen klären",
    "lms.quiz.fuehrung.2.d1": "Die Situation aussitzen",
    "lms.quiz.fuehrung.2.d2": "Sofort eskalieren",
    "lms.quiz.fuehrung.2.d3": "Die Verantwortung einer einzelnen Person zuweisen",
    "lms.quiz.digital.0.stem": "Was ist der wichtigste Grundsatz bei {skill}?",
    "lms.quiz.digital.0.correct": "Das Werkzeug zielgerichtet einsetzen und Resultate kritisch prüfen",
    "lms.quiz.digital.0.d1": "Ergebnisse ungeprüft übernehmen",
    "lms.quiz.digital.0.d2": "Das Werkzeug für alle Aufgaben unverändert nutzen",
    "lms.quiz.digital.0.d3": "Die Verantwortung an die IT abgeben",
    "lms.quiz.digital.1.stem": "Wie gehen Sie im Alltag richtig mit {skill} um?",
    "lms.quiz.digital.1.correct": "Eingaben bewusst wählen und die Ausgabe auf Plausibilität prüfen",
    "lms.quiz.digital.1.d1": "Sensible Daten unbedacht eingeben",
    "lms.quiz.digital.1.d2": "Ergebnisse ohne Prüfung weiterverwenden",
    "lms.quiz.digital.1.d3": "Auf eine Anleitung der IT-Abteilung warten",
    "lms.quiz.digital.2.stem": "Bei {skill} liefert das System ein zweifelhaftes Ergebnis. Was tun Sie?",
    "lms.quiz.digital.2.correct": "Das Ergebnis hinterfragen und bei Unsicherheit nicht übernehmen",
    "lms.quiz.digital.2.d1": "Das Ergebnis trotzdem verwenden",
    "lms.quiz.digital.2.d2": "Das System als fehlerhaft melden und nichts weiter unternehmen",
    "lms.quiz.digital.2.d3": "Die Aufgabe an eine andere Person delegieren",
    "lms.quiz.methodik.0.stem": "Worauf kommt es bei {skill} grundsätzlich an?",
    "lms.quiz.methodik.0.correct": "Strukturiert vorgehen und das Ergebnis regelmässig überprüfen",
    "lms.quiz.methodik.0.d1": "Möglichst schnell zu einem Ergebnis kommen",
    "lms.quiz.methodik.0.d2": "Ohne Planung direkt beginnen",
    "lms.quiz.methodik.0.d3": "Die Methode unabhängig von der Situation gleich anwenden",
    "lms.quiz.methodik.1.stem": "Wie wenden Sie {skill} in einem konkreten Vorhaben an?",
    "lms.quiz.methodik.1.correct": "Ziele klären, Vorgehen planen, Fortschritt laufend überprüfen",
    "lms.quiz.methodik.1.d1": "Nur das Endergebnis kontrollieren",
    "lms.quiz.methodik.1.d2": "Ohne klares Ziel beginnen",
    "lms.quiz.methodik.1.d3": "Den Plan nach dem Start nicht mehr anpassen",
    "lms.quiz.methodik.2.stem": "Ein Vorhaben mit {skill} gerät ins Stocken. Was ist der richtige nächste Schritt?",
    "lms.quiz.methodik.2.correct": "Die Ursache analysieren und das Vorgehen gezielt anpassen",
    "lms.quiz.methodik.2.d1": "Unverändert weitermachen",
    "lms.quiz.methodik.2.d2": "Das Vorhaben ohne Analyse abbrechen",
    "lms.quiz.methodik.2.d3": "Die Verantwortung abgeben",
    "category.regulatorik": "Regulatorik",
    "category.fach": "Fachlich",
    "category.fuehrung": "Führung",
    "category.digital": "Digital",
    "category.methodik": "Methodik",
} satisfies Dict

const en: Record<keyof typeof de, string> = {
    // Shell
    "app.title": "Learning Path",
    "app.badge": "Demo",
    "app.subtitle":
        "Role, regulatory duties, skill profile and performance review are composed into an individual development programme — sequenced, budgeted, and justified for every module.",
    "app.language": "Language",
    "demo.viewSwitch": "View",

    // Landing (Compass)
    "landing.claim": "Personal development plans. From any LMS.",
    "landing.sub":
        "Compass connects to the learning system you already run, understands duties and skill gaps — and composes a justified programme for every person.",
    "landing.cta": "Start the demo",
    "landing.wall.title": "Works with your LMS",
    "landing.wall.sub":
        "One connection, four surfaces: catalogue, transcript, launch, assignment. The LMS stays the system of record.",
    "landing.how.title": "How it works",
    "landing.how.1.title": "Connect",
    "landing.how.1.body": "Plug in the LMS — catalogue and completions flow in automatically.",
    "landing.how.2.title": "Understand",
    "landing.how.2.body": "Detect duties, deadlines and skill gaps per role.",
    "landing.how.3.title": "Guide",
    "landing.how.3.body": "One programme per person — sequenced, budgeted, justified.",
    "landing.personas.title": "One engine, three views",
    "landing.persona.employee": "My learning path, with a \"why this?\" on every module.",
    "landing.persona.manager": "Review-ready proposals instead of blank pages.",
    "landing.persona.hr": "Coverage, gaps and catalogue demand across the whole organisation.",
    "landing.persona.open": "View",
    // Guided path — the demo has five views and no story; this supplies one.
    "tour.start": "Guided tour",
    "tour.step": "Step {current} of {total}",
    "tour.next": "Next",
    "tour.done": "Done",
    "tour.skip": "Skip",
    "tour.plan.title": "One plan, four reasons",
    "tour.plan.body":
        "Sandra Berger's programme. Every module carries its reason: a duty, a prerequisite, a skill gap or a career goal — nothing sits there unexplained.",
    "tour.levers.title": "Move a lever, watch it recompute",
    "tour.levers.body":
        "We just dropped her to 60%. The quarterly budget shrinks with it and the plan is recomputed — not filtered.",
    "tour.manager.title": "The same data, from the other side",
    "tour.manager.body":
        "The line manager's view: proposals ready for the performance review, and confirmation of what a module actually changed on the job.",
    "tour.hr.title": "The whole organisation",
    "tour.hr.body":
        "Coverage, overdue duties and catalogue gaps across every employee — not one profile at a time.",
    "tour.model.title": "Change the model",
    "tour.model.body":
        "We switched on LTEM. Same completions, stricter standard: “evidenced to standard” collapses without a single person having changed.",
    "tour.lms.title": "What employees see",
    "tour.lms.body":
        "Finally, the other side: “What your employees see” opens the simulated learning portal — catalogue, course and evidence.",
    "landing.footnote":
        "Demo — synthetic data on the real organisational structure, no live LMS connection.",

    // Integrations view
    "int.tab": "Integrations",
    "int.title": "Connections",
    "int.subtitle": "Compass reads catalogue and transcript — the LMS stays the system of record.",
    "int.connect": "Connect",
    "int.disconnect": "Disconnect",
    "int.connected": "connected",
    "int.available": "available",
    "int.coming": "planned",
    "int.courses": "{count} courses",
    "int.lastSync": "Last sync: {when}",
    "int.justNow": "just now",
    "cap.catalog": "Catalogue",
    "cap.transcript": "Transcript",
    "cap.launch": "Launch",
    "cap.assign": "Assignment",

    // Connect wizard
    "wiz.title": "Connect Moodle",
    "wiz.step.access": "Access",
    "wiz.step.discovery": "Checks",
    "wiz.step.catalog": "Catalogue",
    "wiz.step.transcript": "Transcript",
    "wiz.final": "Done",
    "wiz.url": "LMS URL",
    "wiz.token": "Web service token",
    "wiz.tokenHint": "Read-only role, limited to course and completion functions.",
    "wiz.next": "Next",
    "wiz.start": "Test connection",
    "wiz.import": "Import catalogue",
    "wiz.sync": "Sync transcript",
    "wiz.done": "Finish",
    "wiz.discovery.1": "Web services reachable",
    "wiz.discovery.2": "core_course_get_courses_by_field",
    "wiz.discovery.3": "core_course_get_contents",
    "wiz.discovery.4": "core_completion_get_activities_completion_status",
    "wiz.discovery.5": "Permissions: read-only",
    "wiz.discovery.6": "Completion tracking enabled",
    "wiz.catalog.running": "Importing courses …",
    "wiz.catalog.mapped": "skills mapped",
    "wiz.catalog.review": "needs review",
    "wiz.catalog.done": "{total} courses imported · {mapped} mapped automatically · {review} for review",
    "wiz.transcript.running": "Reading completions for {count} employees …",
    "wiz.transcript.found": "{count} completions imported",
    "wiz.transcript.note":
        "Moodle serves completions per person and course — the sync runs nightly, plus on demand.",
    "wiz.success": "Moodle is connected. Catalogue and transcript now flow into Compass.",

    // LMS player (mock LTI)
    "player.launch": "Start here",
    "player.via": "via LTI 1.3",
    "player.chrome": "learn.acme-bank.ch · LTI 1.3",
    "player.slide": "Section {current} of {total}",
    "player.nextSlide": "Next",
    "player.finish": "Pass final check",
    "player.passed": "Final check passed",
    "player.close": "Close",
    "toast.ags": "Moodle reports completion (AGS) — transcript updated.",
    "toast.agsNote": "In production up to 30 min delay — instant in the demo.",
    "item.viaLti": "Completed via LTI",

    // Source ripples
    "src.catalogLine": "Catalogue: Moodle ✓ · {count} courses · up to date",
    "src.hrLine": "Source: Moodle ({count} courses) · {sync}",
    "src.demoData": "Catalogue: demo data",
    "levers.moodleTranscript": "Moodle transcript",
    "levers.moodleTranscriptHint": "System of record — always on",

    // HR sync
    "hr.syncNow": "Sync now",
    "hr.syncing": "Syncing …",
    "hr.syncDelta": "+{count} completions imported from Moodle",


    // Personas
    "persona.employee": "Employee",
    "persona.manager": "Line manager",
    "persona.hr": "HR / L&D",

    // Levers
    "levers.title": "Live parameters",
    "levers.reset": "reset",
    "levers.pensum": "Employment level",
    "levers.pensumReal": "actual {value}%",
    "levers.budget": "Budget: {hours} h per quarter",
    "levers.timeBudget": "Time budget at 100%",
    "levers.hoursPerQuarter": "{hours} h/quarter",
    "levers.aspiration": "Career goal",
    "levers.asOf": "Reference date",
    "levers.today": "today",
    "levers.monthsAhead": "+{months} months — refreshers expire",
    "levers.horizon": "Planning horizon",
    "levers.months": "{months} months",
    "levers.sources": "Data sources",
    "levers.sourcesHint":
        "What the proposal may draw on. Without performance data the plan stays valid — it then rests on role and regulatory duties alone.",
    "levers.sensitive": "sensitive",

    // Sources
    "source.stellenprofil": "Job profile",
    "source.lernhistorie": "Learning history",
    "source.cv": "CV",
    "source.mag": "Performance review",
    "source.selbsteinschaetzung": "Self-assessment",

    // Employee view
    "employee.title": "My learning path",
    "employee.stat.items": "Modules",
    "employee.stat.hours": "Total effort",
    "employee.stat.mandatory": "of which mandatory",
    "employee.stat.overdue": "overdue",
    "employee.overdueTitle": "{count} mandatory course overdue",
    "employee.overdueTitle.plural": "{count} mandatory courses overdue",
    "employee.overdueBody":
        "These modules are scheduled regardless of the time budget. A quarter may therefore exceed its budget — that is a visible planning problem, not a silent omission.",
    "employee.quarterBudget": "{planned} / {budget} h",
    "employee.overBudget": "over budget",
    "employee.deferred": "Deferred",
    "employee.deferredHint":
        "Does not fit the time budget within the chosen horizon. Reported deliberately rather than dropped.",
    "employee.noCatalog": "No matching module in the catalogue",
    "employee.gapTable": "Skill gap analysis",
    "employee.load": "Load per quarter",
    "employee.gapsOpen": "{count} open",
    "employee.col.skill": "Skill",
    "employee.col.current": "Current",
    "employee.col.required": "Required",
    "employee.col.origin": "Source",
    "employee.col.evidence": "Evidence",

    // Item card
    "item.why": "Why this?",
    "item.done": "done",
    "item.hours": "{hours} h",
    "item.due": "Due: {date}",
    "item.overdue": "overdue",
    "reason.pflicht": "Mandatory",
    "reason.auffrischung": "Booster",
    "reason.voraussetzung": "Prerequisite",
    "reason.luecke": "Gap",
    "reason.ziel": "Career goal",
    "origin.regulatorik": "Regulatory",
    "origin.rolle": "Role",
    "origin.mag": "Review",
    "origin.ziel": "Goal",

    // Manager view
    "manager.title": "Team development",
    "manager.subtitle": "{count} team members",
    "manager.disclaimer":
        "A proposal for the development conversation — not an assigned plan. The manager adjusts, discusses and adopts it. No automated individual decision is made.",
    "manager.col.person": "Employee",
    "manager.col.role": "Role",
    "manager.col.pensum": "Level",
    "manager.col.items": "Modules",
    "manager.col.hours": "Effort",
    "manager.col.mandatory": "Mandatory",
    "manager.overdueBadge": "{count} overdue",
    "manager.openBadge": "{count} open",
    "manager.fulfilled": "compliant",
    "manager.proposalFor": "Proposal for {name}",
    "manager.adopt": "Add to review",
    "manager.noAction": "No action needed within the chosen horizon.",
    "manager.basis": "Basis: performance review excerpt",
    "manager.matrix": "Mandatory training status",
    "manager.matrixHint": "Hover a column to name the duty",
    "manager.effort": "Effort per team member",

    // HR view
    "hr.title": "L&D cockpit",
    "hr.subtitle": "{count} employees · real organisational structure, synthetic HR attributes",
    "hr.stat.coverage": "Mandatory coverage",
    "hr.stat.coverageHint": "with no open mandatory course",
    "hr.stat.open": "with open duty",
    "hr.stat.openHint": "of which {count} overdue",
    "hr.stat.effort": "Planned effort",
    "hr.stat.effortHint": "≈ {years} FTE years",
    "hr.stat.catalogGaps": "Catalogue gaps",
    "hr.stat.catalogGapsHint": "skills with no module",
    "hr.coverage": "Mandatory training — coverage",
    "hr.overdueSuffix": "{count} overdue",
    "hr.topGaps": "Largest skill gaps",
    "hr.noBaustein": "no module",
    "hr.catalogGapTitle": "Catalogue gap",
    "hr.catalogGapBody":
        "No module exists for {skills} — affecting {count} employees. That is the signal for what L&D should build next.",
    "hr.demand": "Demand per module",
    "hr.byUnit": "Effort by organisational unit",
    "hr.col.unit": "Unit",
    "hr.col.headcount": "Headcount",
    "hr.col.effort": "Effort",
    "hr.col.overdue": "overdue",
    "hr.loadError": "The analysis could not be loaded.",

    // Skill levels
    "level.0": "none",
    "level.1": "foundational",
    "level.2": "working",
    "level.3": "advanced",
    "level.4": "expert",

    // Formats
    "format.e-learning": "E-learning",
    "format.praesenz": "Classroom",
    "format.webinar": "Webinar",
    "format.selbststudium": "Self-study",
    "format.on-the-job": "On the job",
    "format.social": "Social learning",

    // Rationale
    "rat.mandatory": "Mandatory for your role as {role}: {obligation} ({basis}).",
    "rat.dueBy": "Due by {date}.",
    "rat.prerequisite":
        "Prerequisite — without this module you cannot take the content that builds on it.",
    "rat.closesGoal": "Supports your career goal",
    "rat.closesAlso": "Additionally closes",
    "rat.closes": "Closes",
    "rat.fallback": "Recommended based on the requirements of your role as {role}.",
    "rat.src.stellenprofil": "Per your job profile",
    "rat.src.lernhistorie": "Per your learning history",
    "rat.src.cv": "Per your CV",
    "rat.src.mag": "Per your last performance review",
    "rat.src.selbsteinschaetzung": "Per your self-assessment",
    // Steuerung — frameworks
    "persona.steuerung": "Control",
    "steuer.title": "Recommendation engine control",
    "steuer.subtitle": "Pick a framework, tune the parameters — every change recomputes the programmes.",
    "steuer.tab.frameworks": "Frameworks",
    "steuer.tab.params": "Parameters",
    "steuer.family.ladder": "Evaluation models",
    "steuer.family.ladderHint": "Competing answers to the same question — exactly one active.",
    "steuer.family.overlay": "Complementary methods",
    "steuer.family.overlayHint": "Assume an evaluation model underneath, and stack freely.",
    "steuer.family.design": "Learning design",
    "steuer.family.designHint": "Govern how a programme is built, independently of how it is measured.",
    "steuer.changes": "What changes",
    "steuer.watch": "Number to watch",
    "steuer.source": "Source",
    "steuer.active": "Active",
    "steuer.select": "Select",
    "steuer.deselect": "Remove",
    "steuer.adjusted": "adjusted",
    "steuer.conflict": "Only one evaluation model can be active. {names} are mutually exclusive.",
    "steuer.none": "No framework selected — the organisation's default settings apply.",
    "steuer.radar": "Emphasis of this selection",
    "steuer.rule.one": "Exactly one",
    "steuer.rule.any": "Freely combinable",
    "steuer.writes.one": "{count} parameter",
    "steuer.writes.other": "{count} parameters",
    "steuer.wrote.one": "Writes {count} parameter:",
    "steuer.wrote.other": "Writes {count} parameters:",
    "steuer.standard": "House standard",
    "steuer.preview": "Preview",
    "steuer.swap.replacedBy": "replaced by {name}",
    "steuer.radarRule": "Strongest value per axis, not the sum.",
    "steuer.axis.nachweis": "Evidence rigour",
    "steuer.axis.kosten": "Cost focus",
    "steuer.axis.transfer": "Transfer focus",
    "steuer.axis.modalitaet": "Modality range",
    "steuer.axis.wiederholung": "Reinforcement",
    // Steuerung — parameters
    "steuer.cluster.budget": "Time and budget",
    "steuer.cluster.priority": "Prioritisation",
    "steuer.cluster.evidence": "Evidence and impact",
    "steuer.cluster.duties": "Regulatory duties",
    "steuer.cluster.gaps": "Gaps and levels",
    "steuer.cluster.sources": "Data sources and trust",
    "steuer.cluster.sequencing": "Sequencing and reinforcement",
    "steuer.cluster.mix": "Modality mix",
    "steuer.cluster.cost": "Rate card",
    "steuer.p.hoursPerQuarter": "Hours per quarter at 100%",
    "steuer.p.scaleByPensum": "Scale the budget by workload",
    "steuer.p.scaleByPensumHint": "Part-time gets proportionally less learning time — duties are unaffected.",
    "steuer.p.periodMonths": "Length of a planning period",
    "steuer.p.months": "months",
    "steuer.p.originWeight": "Weight by origin",
    "steuer.p.originWeightHint": "Where a requirement comes from decides what it is worth in the ranking.",
    "steuer.p.denominator": "Rank by value per",
    "steuer.p.denominator.hours": "hour",
    "steuer.p.denominator.usd": "dollar",
    "steuer.p.denominator.flat": "raw value",
    "steuer.p.floor": "What counts as evidence",
    "steuer.p.floorHint": "The level at which a completion counts as proof.",
    "steuer.p.gateSkillCredit": "Credit competence only with evidence",
    "steuer.p.gateObligation": "Discharge a duty only with evidence",
    "steuer.p.gateObligationHint": "Regulated duties generally require evidence of knowledge — an attendance record is not evidence.",
    "steuer.p.requireBehaviour": "Require confirmed use on the job",
    "steuer.p.reactionTarget": "Target for likelihood to apply (Level 1)",
    "steuer.p.learningPass": "Pass mark (Level 2)",
    "steuer.p.behaviourWindow": "Confirmation window (Level 3)",
    "steuer.p.resultsWindow": "Results window (Level 4)",
    "steuer.p.days": "days",
    "steuer.p.neverDrop": "Never displace a duty",
    "steuer.p.neverDropHint": "An overbooked quarter is a visible planning problem; a silently dropped duty is an audit finding.",
    "steuer.p.leadTime": "Lead-time factor",
    "steuer.p.leadTimeHint": "Multiplier on each duty's statutory lead time.",
    "steuer.p.minSize": "Smallest gap worth acting on",
    "steuer.p.maxLevelJump": "Largest level jump per module",
    "steuer.p.tooBasic": "Tolerance for modules below the learner's level",
    "steuer.p.trust": "Trust",
    "steuer.p.minTrust": "Minimum trust",
    "steuer.p.sourceMode": "When trust is low",
    "steuer.p.sourceMode.gate": "discard the reading",
    "steuer.p.sourceMode.discount": "discount the level",
    "steuer.p.expand": "Plan prerequisites too",
    "steuer.p.maxDepth": "Maximum chain depth",
    "steuer.p.maxItems": "Modules per period",
    "steuer.p.maxHours": "Hours per module",
    "steuer.p.maxCategories": "Subject areas at once",
    "steuer.p.maxCategoriesHint": "Interleaving limit: four subjects in parallel work worse than two in sequence.",
    "steuer.p.deferralMode": "Defer when it does not fit",
    "steuer.p.deferralMode.chain": "check the whole chain",
    "steuer.p.deferralMode.period": "period by period",
    "steuer.p.reinforcement": "Schedule boosters",
    "steuer.p.afterDays": "Delay after the course",
    "steuer.p.boosterHours": "Length of a booster",
    "steuer.p.mixEnabled": "Steer the modality mix",
    "steuer.p.mixTarget": "Target split",
    "steuer.p.strength": "Steering strength",
    "steuer.mod.on-the-job": "On the job",
    "steuer.mod.social": "With others",
    "steuer.mod.formal": "In a course",
    "steuer.p.perHour": "USD per hour",
    "steuer.p.fixed": "USD fixed",
    "steuer.p.opportunity": "Learners' working time",
    "steuer.p.opportunityHint": "Opportunity cost per learning hour. Zero means: not counted.",
    "steuer.p.honourOverride": "Prefer catalogue prices",
    "steuer.inactive": "No effect until the ranking is denominated in dollars.",
    "steuer.mixInactive": "No effect until the modality mix is being steered.",
    // Steuerung — impact
    "steuer.impact": "Impact",
    "steuer.impact.subtitle": "Computed on the real model, not estimated.",
    "steuer.impact.person": "Example employee",
    "steuer.impact.population": "Whole organisation",
    "steuer.impact.items": "Modules",
    "steuer.impact.hours": "Hours",
    "steuer.impact.deferred": "Deferred",
    "steuer.impact.coverage": "Duty coverage",
    "steuer.impact.overdue": "Overdue",
    "steuer.impact.cost": "Programme cost",
    "steuer.impact.added": "new",
    "steuer.impact.removed": "dropped",
    "steuer.impact.moved": "moved",
    "steuer.impact.unchanged": "No change from the standard settings.",
    "steuer.impact.loading": "computing",
    "steuer.impact.evidenceShare": "Evidenced to standard",
    "steuer.impact.same": "unchanged",
    "steuer.chart.distribution": "Distribution of learning hours",
    "steuer.chart.distributionHint": "All {count} people by planned effort. An average hides whether everyone moves a little or a few move a great deal.",
    "steuer.chart.flow": "Where the budget flows",
    "steuer.chart.flowHint": "From why an hour was planned to how it is delivered. Width is hours.",
    "steuer.chart.standard": "Standard",
    "steuer.chart.tuned": "Current settings",
    "steuer.chart.hoursAxis": "Planned hours per person",
    "steuer.chart.people": "People",
    "steuer.overrides": "{count} parameters differ from standard",
    "steuer.overrides.none": "All parameters at standard",
    "steuer.reset": "Reset to standard",
    "steuer.presetNote": "A framework writes parameters. Every one of them stays visible and editable under Parameters.",
    "steuer.cluster.governance": "Governance",
    "steuer.governance.body": "The composer is deterministic: the same data and the same parameters produce the same plan, defensible to internal audit. Nothing disappears quietly — whatever does not fit the budget is shown as deferred. Every recommendation carries its reasoning. The language model reads free text and writes the wording; it does not decide who must learn what.",
    "instrument.abschluss": "Completion",
    "instrument.wissenstest": "Knowledge check",
    "instrument.entscheidungstest": "Decision test",
    "instrument.transfernachweis": "Transfer evidence",
    "wirkung.title": "Impact measurement",
    "wirkung.evidenceMix": "What the learning record proves",
    "wirkung.evidenceMixHint": "{count} recorded completions, by strength of evidence.",
    "wirkung.floorNote": "At the currently required level “{floor}”, {share}% of completions clear the bar.",
    "wirkung.chain": "Chain of evidence",
    "wirkung.chainHint": "Share of completions with evidence at each level. These are independent measurements, not a funnel — mandatory training almost always records a test result and far less often a response.",
    "wirkung.l1": "Response recorded",
    "wirkung.l1target": "Likelihood to apply on target",
    "wirkung.l2": "Assessment passed",
    "wirkung.bridge": "Transfer deliverable met",
    "wirkung.l3": "Use on the job confirmed",
    "wirkung.l3verified": "of which manager-verified",
    "wirkung.l4": "Level 4 — business results",
    "wirkung.l4Hint": "Measured {from}–{to} days after completion.",
    "wirkung.indicator": "Indicator",
    "wirkung.before": "Before",
    "wirkung.after": "Current",
    "wirkung.change": "Change",
    "wirkung.kpi.auditfeststellungen": "Audit findings",
    "wirkung.kpi.dokumentation": "Rework on customer documentation",
    "wirkung.kpi.fehlerquote": "Error rate in core processes",
    "wirkung.kpi.produktivitaet": "Days to full productivity",
    "wirkung.kpi.servicequalitaet": "Service quality (customer survey)",
    "wirkung.benefit": "Adjusted benefit",
    "wirkung.cost": "Programme cost",
    "wirkung.roi": "ROI",
    "wirkung.method": "Gross benefit USD {gross}, adjusted by the isolation factor ({isolation}%) and the confidence discount ({confidence}%) per Phillips. Benefit-cost ratio {bcr}. Every indicator scales with confirmed transfer ({transfer}%) — not with attendance.",
    "wirkung.queue": "Confirm transfer",
    "wirkung.queueHint": "Due {from}–{to} days after completion. A confirmation becomes Level 3 evidence immediately and changes that person's plan.",
    "wirkung.queueEmpty": "Nothing is awaiting confirmation right now.",
    "wirkung.completedOn": "completed on",
    "wirkung.confirmed": "confirmed",
    "wirkung.drivers": "What support was in place?",
    "wirkung.driver.monitor": "Monitored",
    "wirkung.driver.reinforce": "Reinforced",
    "wirkung.driver.encourage": "Encouraged",
    "wirkung.driver.reward": "Recognised",
    "wirkung.applied.yes": "Being applied",
    "wirkung.applied.no": "Not being applied",
    "wirkung.kette": "Chain of evidence",
    "wirkung.none": "no evidence",
    "category.regulatorik": "Regulatory",
    "category.fach": "Professional",
    "category.fuehrung": "Leadership",
    "category.digital": "Digital",
    "category.methodik": "Method",
    "lms.shell.title": "Learning portal",
    "lms.shell.close": "Close",
    "lms.shell.tab.catalog": "Catalogue",
    "lms.shell.tab.transcript": "My courses",
    "lms.shell.back": "Back to catalogue",
    "lms.catalog.search": "Search courses…",
    "lms.catalog.count": "{count} courses",
    "lms.catalog.filter.format": "Format",
    "lms.catalog.filter.category": "Subject area",
    "lms.catalog.filter.provider": "Provider",
    "lms.catalog.filter.all": "All",
    "lms.catalog.empty": "No courses found.",
    "lms.detail.durationLabel": "Duration",
    "lms.detail.formatLabel": "Format",
    "lms.detail.providerLabel": "Provider",
    "lms.detail.levelLabel": "Entry level",
    "lms.detail.skillsLabel": "Skills taught",
    "lms.detail.prerequisitesLabel": "Prerequisites",
    "lms.detail.enrolled": "{count} people have taken this course",
    "lms.detail.courseId": "Course ID {id}",
    "lms.detail.start": "Start course",
    "lms.detail.notOnline": "This course type isn't run online. Register through {provider}.",
    "lms.detail.alreadyDone": "Already completed",
    "lms.player.quizIntro": "Quick knowledge check — {count} questions",
    "lms.player.question": "Question {current} of {total}",
    "lms.player.showResult": "Show result",
    "lms.player.reactionIntro": "Two more quick questions about your take",
    "lms.player.reaction.relevance": "How relevant was this course to your work?",
    "lms.player.reaction.apply": "How likely are you to apply what you learned?",
    "lms.player.scaleLow": "low",
    "lms.player.scaleHigh": "high",
    "lms.player.result.title": "Result",
    "lms.player.result.score": "{correct} of {total} correct ({percent}%)",
    "lms.player.result.failed": "Knowledge check not passed — the completion is still recorded.",
    "lms.transcript.empty": "No completed courses yet.",
    "lms.transcript.completedOn": "Completed on {date}",
    "lms.transcript.evidence.attendance": "Attendance only",
    "lms.transcript.evidence.learning": "{instrument} · {percent}%",
    "lms.transcript.evidence.reaction": "Feedback recorded",
    "lms.integrations.preview.title": "What your employees see",
    "lms.integrations.preview.body": "A simulated view of the learning portal Compass reaches into — catalogue, course launch and transcript, from the learner's side.",
    "lms.integrations.preview.cta": "Open the learning portal",
    "instrument.abschluss.short": "Attendance",
    "instrument.wissenstest.short": "Knowledge check",
    "instrument.entscheidungstest.short": "Decision test",
    "instrument.transfernachweis.short": "Transfer evidence",
    "lms.quiz.regulatorik.0.stem": "What is the main goal of {skill}?",
    "lms.quiz.regulatorik.0.correct": "Reduce risk for clients and the bank by applying the rules correctly",
    "lms.quiz.regulatorik.0.d1": "Document administrative effort only",
    "lms.quiz.regulatorik.0.d2": "Matter only during an external audit",
    "lms.quiz.regulatorik.0.d3": "A formality unrelated to daily work",
    "lms.quiz.regulatorik.1.stem": "How do you correctly handle {skill} day to day?",
    "lms.quiz.regulatorik.1.correct": "Apply the rules consistently and escalate uncertainty to the right team",
    "lms.quiz.regulatorik.1.d1": "Apply the rules only when time allows",
    "lms.quiz.regulatorik.1.d2": "Decide on deviations alone, without consulting anyone",
    "lms.quiz.regulatorik.1.d3": "The rules only apply to new client relationships",
    "lms.quiz.regulatorik.2.stem": "You hit an edge case involving {skill}. What is correct?",
    "lms.quiz.regulatorik.2.correct": "Document the case and clarify it with the responsible team",
    "lms.quiz.regulatorik.2.d1": "Decide the case on your own authority",
    "lms.quiz.regulatorik.2.d2": "Ignore it if it is rare",
    "lms.quiz.regulatorik.2.d3": "Wait until the client asks",
    "lms.quiz.fach.0.stem": "What matters most for {skill}?",
    "lms.quiz.fach.0.correct": "Assess the client's individual situation as a whole",
    "lms.quiz.fach.0.d1": "Always recommend the highest-margin option",
    "lms.quiz.fach.0.d2": "Offer only standard products",
    "lms.quiz.fach.0.d3": "Keep the advisory depth as shallow as possible",
    "lms.quiz.fach.1.stem": "How do you correctly apply {skill} in a client conversation?",
    "lms.quiz.fach.1.correct": "Clarify needs, present options clearly, leave the decision to the client",
    "lms.quiz.fach.1.d1": "Propose a single product immediately",
    "lms.quiz.fach.1.d2": "Use jargon without explaining it",
    "lms.quiz.fach.1.d3": "Make the decision on the client's behalf",
    "lms.quiz.fach.2.stem": "A client situation involving {skill} is unusual. What do you do?",
    "lms.quiz.fach.2.correct": "Discuss the case with a specialist team or an experienced colleague",
    "lms.quiz.fach.2.d1": "Apply the standard solution anyway",
    "lms.quiz.fach.2.d2": "Decline the request",
    "lms.quiz.fach.2.d3": "Grant an exception on your own",
    "lms.quiz.fuehrung.0.stem": "What matters most about {skill} for a manager?",
    "lms.quiz.fuehrung.0.correct": "Communicate clearly and distribute responsibility sensibly across the team",
    "lms.quiz.fuehrung.0.d1": "Make every decision centrally yourself",
    "lms.quiz.fuehrung.0.d2": "Avoid conflict for as long as possible",
    "lms.quiz.fuehrung.0.d3": "Give feedback only once a year",
    "lms.quiz.fuehrung.1.stem": "How do you put {skill} into practice day to day?",
    "lms.quiz.fuehrung.1.correct": "Listen regularly, clarify expectations, and give timely feedback",
    "lms.quiz.fuehrung.1.d1": "Only start a conversation when there is a problem",
    "lms.quiz.fuehrung.1.d2": "Communicate decisions without explaining them",
    "lms.quiz.fuehrung.1.d3": "Delegate the responsibility entirely",
    "lms.quiz.fuehrung.2.stem": "A difficult situation involving {skill} comes up on the team. What is the right first step?",
    "lms.quiz.fuehrung.2.correct": "Have the direct conversation and clarify the different viewpoints",
    "lms.quiz.fuehrung.2.d1": "Let the situation sit",
    "lms.quiz.fuehrung.2.d2": "Escalate immediately",
    "lms.quiz.fuehrung.2.d3": "Assign responsibility to a single person",
    "lms.quiz.digital.0.stem": "What is the most important principle for {skill}?",
    "lms.quiz.digital.0.correct": "Use the tool with intent and check the results critically",
    "lms.quiz.digital.0.d1": "Accept the output without checking it",
    "lms.quiz.digital.0.d2": "Use the tool the same way for every task",
    "lms.quiz.digital.0.d3": "Hand the responsibility to IT",
    "lms.quiz.digital.1.stem": "How do you correctly handle {skill} day to day?",
    "lms.quiz.digital.1.correct": "Choose inputs deliberately and check the output for plausibility",
    "lms.quiz.digital.1.d1": "Enter sensitive data without thinking",
    "lms.quiz.digital.1.d2": "Reuse results without review",
    "lms.quiz.digital.1.d3": "Wait for instructions from IT",
    "lms.quiz.digital.2.stem": "For {skill}, the system returns a questionable result. What do you do?",
    "lms.quiz.digital.2.correct": "Question the result and do not use it while unsure",
    "lms.quiz.digital.2.d1": "Use the result anyway",
    "lms.quiz.digital.2.d2": "Report the system as faulty and do nothing further",
    "lms.quiz.digital.2.d3": "Delegate the task to someone else",
    "lms.quiz.methodik.0.stem": "What matters most for {skill}?",
    "lms.quiz.methodik.0.correct": "Work in a structured way and review the outcome regularly",
    "lms.quiz.methodik.0.d1": "Reach an outcome as quickly as possible",
    "lms.quiz.methodik.0.d2": "Start without any planning",
    "lms.quiz.methodik.0.d3": "Apply the method the same way regardless of the situation",
    "lms.quiz.methodik.1.stem": "How do you apply {skill} in a concrete project?",
    "lms.quiz.methodik.1.correct": "Clarify goals, plan the approach, review progress continuously",
    "lms.quiz.methodik.1.d1": "Only check the final outcome",
    "lms.quiz.methodik.1.d2": "Start without a clear goal",
    "lms.quiz.methodik.1.d3": "Never adjust the plan once it starts",
    "lms.quiz.methodik.2.stem": "A project involving {skill} stalls. What is the right next step?",
    "lms.quiz.methodik.2.correct": "Analyse the cause and adjust the approach deliberately",
    "lms.quiz.methodik.2.d1": "Keep going unchanged",
    "lms.quiz.methodik.2.d2": "Abandon it without analysis",
    "lms.quiz.methodik.2.d3": "Hand off the responsibility",
}

export type UiKey = keyof typeof de

const DICTS: Record<Lang, Record<UiKey, string>> = { de, en }

/**
 * Translate a key, interpolating `{name}` placeholders.
 *
 * Returns the key itself when missing so a gap shows up on screen rather than
 * rendering as an empty element — on a demo stage a visible `hr.demand` is far
 * easier to spot and fix than a silently blank heading.
 */
export function translate(
    lang: Lang,
    key: UiKey,
    values?: Record<string, string | number>,
): string {
    const template = DICTS[lang][key] ?? key
    if (!values) return template
    return template.replace(/\{(\w+)\}/g, (match, name: string) =>
        name in values ? String(values[name]) : match,
    )
}

export type Translator = (key: UiKey, values?: Record<string, string | number>) => string

export function createTranslator(lang: Lang): Translator {
    return (key, values) => translate(lang, key, values)
}
